import * as client from '@/core/client';
import { EDITOR_API_KEY, EDITOR_API_PARAM, EDITOR_TOKEN_KEY, EDITOR_TOKEN_PARAM } from '@/editor/keys';

type Call = (...args: never[]) => unknown;
type Snippet = ((...args: unknown[]) => void) & { q?: ArrayLike<unknown>[] };
interface SnippetHost {
  onboardsight?: Snippet;
}

const api: Record<string, Call> = client;

/** Replaces the snippet stub with a dispatcher and replays the calls queued before the bundle arrived. */
export function installSnippet(host: SnippetHost): void {
  const queued = host.onboardsight?.q ?? [];
  const dispatch: Snippet = (method, ...args) => {
    if (typeof method !== 'string' || !Object.prototype.hasOwnProperty.call(api, method)) return;
    (api[method] as (...args: unknown[]) => unknown)(...args);
  };
  host.onboardsight = dispatch;
  for (const call of queued) dispatch(...Array.from(call));
}

/**
 * True when an editor token is present: moves `onb_editor` (and the control-plane
 * origin in `onb_api`) from the query string into sessionStorage and strips
 * both from the address bar, or finds a token stored earlier in this tab.
 */
export function detectEditor(): boolean {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get(EDITOR_TOKEN_PARAM);
    if (token) {
      window.sessionStorage.setItem(EDITOR_TOKEN_KEY, token);
      const api = url.searchParams.get(EDITOR_API_PARAM);
      if (api) window.sessionStorage.setItem(EDITOR_API_KEY, api);
      else window.sessionStorage.removeItem(EDITOR_API_KEY);
    }
    if (url.searchParams.has(EDITOR_TOKEN_PARAM) || url.searchParams.has(EDITOR_API_PARAM)) {
      url.searchParams.delete(EDITOR_TOKEN_PARAM);
      url.searchParams.delete(EDITOR_API_PARAM);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    return window.sessionStorage.getItem(EDITOR_TOKEN_KEY) !== null;
  } catch {
    return false;
  }
}

/** The editor chunk is fetched only when a token exists; failures never reach the host page. */
export function loadEditor(): void {
  import('@/editor')
    .then((editor) => editor.startEditor())
    .catch(() => undefined);
}

if (typeof window !== 'undefined') {
  installSnippet(window as unknown as SnippetHost);
  if (detectEditor()) loadEditor();
}
